import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ValidationCheckType, ValidationStatus, ValidationTestResult } from "@app/main/modules/foundation/_services/device-data-validation.service";
import { IDateQueryOptions } from "@app/services/accelerometerRecord.service";
import { TranslationService } from "@app/services/translation.service";
import { Observable, Subject } from "rxjs";
import { map, tap } from "rxjs/operators";

export enum StringSearchType {
    Match,
    Contains,
    EndsWith,
    StartsWith,
}

export interface IStringSearchOption {
    Value: string
    Type: StringSearchType
}

export interface IVehicleValidationCheckRecordQueryOptions extends IDateQueryOptions {
    CommissionId?: number;
    VehicleId?: number;
    Serial?: IStringSearchOption;
    LatestByValidationCheckType?: boolean;
}

export interface IVehicleValidationCheckRecord {
    Id?: number;
    ValidationCheckType: ValidationCheckType;
    EventTime?: Date;
    UserId?: number;
    VehicleId?: number;
    Serial?: string;
    CommissionId?: number;
    ValidationCheckResultJson?: string;
}

@Injectable({
    providedIn: 'root'
})
export class VehicleValidationCheckRecordService {
    checkRecordListSubject = new Subject<{ [key: number]: IVehicleValidationCheckRecord[] }>();

    private checkRecordsList: { [key: number]: IVehicleValidationCheckRecord[] } = {};

    constructor(
        private readonly http: HttpClient,
        private readonly translationService: TranslationService
    ) { }

    getVehicleValidationCheckRecords(queryOptions: IVehicleValidationCheckRecordQueryOptions = {}): Observable<IVehicleValidationCheckRecord[]> {
        const params = this.constructHttpParams(queryOptions);

        return this.http.get<IVehicleValidationCheckRecord[]>(`/api/foundation/vehiclevalidationcheck`, { params: params as HttpParams })
            .pipe(tap(checkRecords => {
                checkRecords.forEach(checkRecord => {
                    this.addCheckRecordToCachedJobItemList(checkRecord);
                });
                this.checkRecordListSubject.next(this.checkRecordsList);
            }));
    }

    addVehicleValidationCheckRecords(vehicleValidationCheckRecord: IVehicleValidationCheckRecord): Observable<IVehicleValidationCheckRecord> {
        return this.http.post<IVehicleValidationCheckRecord>(`/api/foundation/vehiclevalidationcheck`, vehicleValidationCheckRecord)
            .pipe(tap(checkRecord => {
                const newCheckRecord = checkRecord;
                this.addCheckRecordToCachedJobItemList(newCheckRecord);
                this.checkRecordListSubject.next(this.checkRecordsList);
            }));
    }

    associateVehicleValidationCheckRecordsToCommission(checkRecordIds: number[], commissionId: number): Observable<void> {
        return this.http.post<void>('/api/foundation/vehiclevalidationcheck/associate', checkRecordIds, { params: { commissionId } });
    }

    getRecordResultsForTable(commission): Observable<string> {
        return this.getVehicleValidationCheckRecords({
            CommissionId: commission.Id
        }).pipe(
            map(records => {
                let { passCount, failCount, warningCount } = this.getRecordStatistics(records);

                if (!passCount && !failCount && !warningCount) {
                    return this.translationService.t("Foundation.NoTestsRun");
                }

                return this.translationService.t("Foundation.XPassedXFailedXWarning", [passCount, failCount, warningCount]);
            })
        );
    }

    // ------------------------------

    private constructHttpParams(queryOptions: IVehicleValidationCheckRecordQueryOptions = {}): HttpParams {
        let params = new HttpParams();

        for (const key in queryOptions) {
            if (queryOptions.hasOwnProperty(key)) {
                const value = queryOptions[key];
                if (typeof value === 'object') {
                    // Handle nested objects
                    for (const nestedKey in value) {
                        if (value.hasOwnProperty(nestedKey)) {
                            params = params.append(`${key}.${nestedKey}`, value[nestedKey]);
                        }
                    }
                } else {
                    params = params.append(key, value);
                }
            }
        }

        return params;
    }

    private addCheckRecordToCachedJobItemList(checkRecord: IVehicleValidationCheckRecord): void {
        const checkRecords = this.getCachedCheckRecordsForVehicle(checkRecord.VehicleId);

        if (!checkRecords?.length) {
            this.checkRecordsList[checkRecord.VehicleId] = [checkRecord];
            return;
        }

        const existing = checkRecords.findIndex(cr => cr.Id === checkRecord.Id);
        if (existing < 0) {
            checkRecords.push(checkRecord);
            return;
        }

        checkRecords.splice(existing, 1);
        checkRecords.push(checkRecord);
    }

    private getCachedCheckRecordsForVehicle(vehicleId: number): IVehicleValidationCheckRecord[] {
        return this.checkRecordsList[vehicleId];
    }

    private getRecordStatistics(records: IVehicleValidationCheckRecord[]): { passCount: number, failCount: number, warningCount: number } {
        let passCount = 0;
        let failCount = 0;
        let warningCount = 0;

        records.forEach(record => {
            const parsedResults = JSON.parse(record.ValidationCheckResultJson);

            parsedResults.forEach(parsedResult => {
                if (!parsedResult['Status']) {
                    return;
                }
                switch (parsedResult['Status']) {
                    case ValidationStatus.Pass:
                        passCount++;
                        break;
                    case ValidationStatus.Fail:
                        failCount++;
                        break;
                    case ValidationStatus.Warning:
                        warningCount++;
                        break;
                }

            })
        })

        return {
            passCount,
            failCount,
            warningCount
        }
    }
}
